import dayjs from 'dayjs'
import { useCallback, useMemo } from 'react'
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from 'recharts'
import {
  NameType,
  ValueType,
} from 'recharts/types/component/DefaultTooltipContent'
import {
  ChartContainer,
  ChartTitle,
  ToolTipContainer,
  TooltipInnerContainer,
} from '../../containers/PatientsView/PatientTabs/RecordedEcgTab/RecordedEcgTabStyle'
import { VitalsChartData } from '../../types/SensorType'

type ChartName = 'spo2' | 'pr' | 'resp' | 'temp' | 'mews' | 'bp'
type ChartKey = Exclude<ChartName, 'bp'> | 'dia' | 'sys'
type LiveChartProp = {
  lineChartDataKey?: ChartKey
  lineChartDataName: ChartName
  liveData: VitalsChartData[ChartName]
  multiLineChartKey?: ChartKey[]
}

export default function LiveDataChart({
  lineChartDataKey,
  lineChartDataName,
  liveData,
  multiLineChartKey,
}: LiveChartProp) {
  const chartData = useMemo(() => {
    return liveData.map((data, index) => {
      const nextIndex = index + 1 < liveData.length ? index + 1 : index    
      // Converting UTC to local time zone  
      let date = dayjs.tz(data.time, "UTC").tz(dayjs.tz.guess());

      if (lineChartDataKey) {

        let value = valueManipulation(
          `${data[lineChartDataKey as keyof typeof data]}`,
          lineChartDataKey
        )

        return {
          [`${lineChartDataKey}-${getScore(data, lineChartDataKey)}`]: value,
          [`${lineChartDataKey}-${getScore(liveData[nextIndex], lineChartDataKey)}`]:
            value,
          [`${lineChartDataKey}`]: value,
          iscore: getScore(liveData[nextIndex], lineChartDataKey),
          time: dayjs(date).valueOf(),
        }
      }

      if (multiLineChartKey) {
        let chartData: Record<string, string | number | undefined> = {}
        multiLineChartKey.forEach((key) => {
          let value = valueManipulation(
            `${data[key as keyof typeof data]}`,
            key
          )
          let obj = {
            [`${key}-${getScore(data, key)}`]: value,
            [`${key}-${getScore(liveData[nextIndex], key)}`]: value,
            [`${key}`]: value,
          }
          chartData = { ...chartData, ...obj }
        })
        return {
          ...chartData,
          time: dayjs(date).valueOf(),
        }
      } else {
        return {
          time: dayjs(date).valueOf(),
        }
      }
    })
  }, [lineChartDataKey, liveData, multiLineChartKey])

  const yAxisDomain = useMemo(() => {
    if (lineChartDataKey) {
      if (lineChartDataKey === 'mews') {
        return [-1, 3]
      }

      if (lineChartDataKey === 'resp') {
        return ['dataMin - 25', 'dataMax + 25']
      }

      return ['dataMin - 2', 'dataMax + 2']
    }

    if (multiLineChartKey) {
      return ['dataMin - 25', 'dataMax + 25']
    }
  }, [lineChartDataKey, multiLineChartKey])

  const CustomTooltip = useCallback(
    ({ active, payload }: TooltipProps<ValueType, NameType>) => {
      if (active && payload && payload.length) {
        const time = dayjs(payload[0].payload.time).format('HH:mm:ss')
        return (
          <ToolTipContainer>
            {multiLineChartKey ? (
              <TooltipInnerContainer>
                {payload.map((key, index) => {
                  return (
                    multiLineChartKey[index] &&
                    index < multiLineChartKey.length && (
                      <span>
                        <strong> {multiLineChartKey[index]} </strong>{' '}
                        {`${key.payload[`${multiLineChartKey[index]}`]}`}
                      </span>
                    )
                  )
                })}
                <span>
                  <strong> Time </strong> {time}
                </span>
              </TooltipInnerContainer>
            ) : (
              <TooltipInnerContainer>
                <span>
                  <strong> {lineChartDataKey} </strong>{' '}
                  {`${payload[0].payload[`${lineChartDataKey}`]}`}
                </span>
                <span>
                  <strong> Time </strong> {time}
                </span>
              </TooltipInnerContainer>
            )}
          </ToolTipContainer>
        )
      }

      return <></>
    },
    [lineChartDataKey, multiLineChartKey]
  )

  const hasValidIscore = chartData.some(
    (data) => data.iscore !== undefined && data.iscore >= 0
  )
  const isValidForLineChart = !!lineChartDataKey && hasValidIscore
  const isValidForMultiLineChart = !!multiLineChartKey && chartData.length > 0
  const hasData = isValidForLineChart || isValidForMultiLineChart

  return (
    <ChartContainer>
      <ChartTitle>{getLiveChartTitle(lineChartDataName, chartData)}</ChartTitle>
      <ResponsiveContainer width='100%' height='100%'>
        <LineChart data={hasData ? chartData : []}>
          <CartesianGrid strokeDasharray='3 3' />

          {hasData && (
            <XAxis
              type='number'
              tickFormatter={(value) =>
                dayjs(value).format(
                  multiLineChartKey ? 'HH:mm:ss.SSS' : 'HH:mm:ss'
                )
              }
              dataKey='time'
              domain={hasData ? chartData.map(({ time }) => time) : undefined}
            />
          )}

          {hasData && <YAxis dataKey={lineChartDataKey} domain={yAxisDomain} />}

          <Tooltip content={<CustomTooltip />} cursor={hasData} />
          {lineChartDataKey &&
            ['black', 'green', '#fff991', '#ffcc6c', '#ff6d6d'].map(
              (color, index) => {
                let score = index - 1

                return (
                  <Line
                    key={color}
                    isAnimationActive={false}
                    name={`${lineChartDataKey}`}
                    type='linear'
                    strokeWidth={3}
                    dataKey={`${lineChartDataKey}-${score}`}
                    stroke={color}
                    dot={false}
                    activeDot={{ r: 0 }}
                  />
                )
              }
            )}

          {multiLineChartKey &&
            multiLineChartKey.map((key) =>
              ['black', 'green', '#fff991', '#ffcc6c', '#ff6d6d'].map(
                (color, index) => {
                  let score = index - 1

                  const diaColor =
                    key === 'dia' && score === 0 ? '#000080' : null

                  return (
                    <Line
                      isAnimationActive={false}
                      name={`${key}`}
                      type='linear'
                      strokeWidth={3}
                      dataKey={`${key}-${score}`}
                      stroke={diaColor ?? color}
                      dot={false}
                      activeDot={{ r: 0 }}
                    />
                  )
                }
              )
            )}
        </LineChart>
      </ResponsiveContainer>
    </ChartContainer>
  )
}

function getLiveChartTitle(chartName: ChartName, chartData: any) {
  if (!chartData) return ''
  let lastChartData = chartData[chartData.length - 1]

  if (chartName === 'mews') {
    let value = lastChartData?.[chartName]
    if (value !== 0 && !value) return 'HS : NO DATA'
    return `HS : ${value}`
  } else if (chartName === 'spo2') {
    let value = parseFloat(lastChartData?.[chartName])
    if (!value) return `SpO₂ : NO DATA`;
    return `SpO₂ : ${value}%`
  } else if (chartName === 'pr') {
    let value = parseFloat(lastChartData?.[chartName])
    if (!value) return 'PR : NO DATA'
    return `PR : ${value} bpm`
  } else if (chartName === 'temp') {
    let value = parseFloat(lastChartData?.[chartName])
    if (!value) return 'Temp : NO DATA'
    return `Temp : ${value} °F`
  } else if (chartName === 'resp') {
    let value = parseFloat(lastChartData?.[chartName])
    if (!value) return 'Resp : NO DATA'
    return `Resp : ${value} bpm`
  } else if (chartName === 'bp') {
    let sysValue = parseFloat(lastChartData?.['sys'])
    let diaValue = parseFloat(lastChartData?.['dia'])
    if (!sysValue || !diaValue) return 'BP : NO DATA'
    return `BP : ${sysValue} mmHg/${diaValue} mmHg`
  }
  return ''
}

function parseNumber(value: string) {
  if (!value || isNaN(parseInt(value))) return 0

  return parseInt(value)
}

function getScore(data: VitalsChartData[ChartName][number], key: ChartKey) {
  let score = 0

  if (key === 'dia' || key === 'sys') {
    score = parseNumber(`${data[`bpScore` as keyof typeof data]}`)
  }

  if (key === 'mews') {
    score = parseNumber(`${data[`mews` as keyof typeof data]}`)
  } else {
    score = parseNumber(`${data[`${key}Score` as keyof typeof data]}`)
  }

  return score >= 3 ? 3 : score
}

function valueManipulation(value: string, key: ChartKey) {
  let numValue = key === 'temp' ? parseFloat(value) : parseInt(value)
  if (key === 'mews') return numValue
  if (!numValue) return undefined
  return numValue
}
